import { Navigate, useLocation, type Location } from 'react-router-dom'
import { Spinner } from '@/components/ui/spinner'
import { useAuth } from '@/hooks/use-auth'

/** Public shell route used under the sign-in modal (must not itself require auth). */
function publicBackground(location: Location): Location {
  return {
    ...location,
    pathname: '/search',
    search: '',
    hash: '',
    state: null,
    key: 'signin-bg',
  }
}

/**
 * Redirects unauthenticated visitors (including guests) to the sign-in modal.
 * Use for flows that need a real Firebase user.
 *
 * Background is always `/search` so the protected page is not rendered under the
 * modal (that would re-fire this Navigate and crash with a redirect loop).
 */
export function RequireAuth({ children }: { children: React.ReactNode }) {
  const { user, isGuest, isLoading } = useAuth()
  const location = useLocation()

  if (isLoading) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <Spinner />
      </div>
    )
  }

  if (!user || isGuest) {
    const next = `${location.pathname}${location.search}`
    return (
      <Navigate
        to={`/signin?next=${encodeURIComponent(next)}`}
        replace
        state={{ background: publicBackground(location) }}
      />
    )
  }

  return <>{children}</>
}
